/**
 * HomeScreen Store - Zustand Implementation
 *
 * Manages the registry of tiles displayed on the iOS-style home screen.
 * Supports user-customized tile layout (reorder & move between sections)
 * persisted as JSON in localStorage for easy extensibility.
 */

import { create } from 'zustand';
import { devtools, persist, createJSONStorage } from 'zustand/middleware';
import { createLogger } from '../utils/logger';

const logger = createLogger('HomeScreenStore');

export interface HomeTile {
  id: string;
  label: string;
  icon: string;
  route: string;
  section?: 'main' | 'dock' | string;
  order?: number;
  visible?: () => boolean;
  badge?: () => string | number | null;
  /** Optional media registry ID — when set, MediaIcon lazy-loads from R2 */
  mediaId?: string;
}

export interface HomeSection {
  id: string;
  title?: string;
  order: number;
  items: HomeTile[];
}

/**
 * Two tiles are considered equivalent for store-update purposes when every
 * displayed field matches. Function fields (visible/badge) use reference
 * equality — callers register stable function refs (or none).
 *
 * When this returns true, registerTile() skips the state update, which
 * prevents the "tiles rearrange on every refetch" symptom because nothing
 * actually changed.
 */
function tilesEquivalent(a: HomeTile | undefined, b: HomeTile): boolean {
  if (!a) return false;
  return (
    a.id === b.id &&
    a.label === b.label &&
    a.icon === b.icon &&
    a.route === b.route &&
    a.section === b.section &&
    a.order === b.order &&
    a.mediaId === b.mediaId &&
    a.visible === b.visible &&
    a.badge === b.badge
  );
}

/**
 * User-customized position for a single tile.
 * Stored separately from tile registration data so it survives
 * re-registration cycles (catalog refresh, plugin reload, etc.)
 *
 * Storage format: JSON object { [tileId]: TileLayoutEntry }
 * Easy to extend with future fields (page, folder, size, etc.)
 */
export interface TileLayoutEntry {
  section: string;   // 'main' | 'dock' | custom section id
  order: number;     // sort position within section
}

/** Full layout map - JSON-serializable */
export type TileLayout = Record<string, TileLayoutEntry>;

interface HomeScreenState {
  items: Map<string, HomeTile>;
  sections: Map<string, HomeSection>;
  /** Admin icon overrides - map of tile ID to icon (emoji, URL, or data URI) */
  iconOverrides: Record<string, string>;
  /**
   * User-customized tile positions.
   * When non-empty, overrides the default section/order from registerTile().
   * Persisted as plain JSON for easy debugging and future migration.
   */
  tileLayout: TileLayout;
  registerTile: (tile: HomeTile) => () => void;
  /** Register multiple tiles in a single state update (avoids N re-renders) */
  registerTiles: (tiles: HomeTile[]) => void;
  unregisterTile: (tileId: string) => void;
  registerSection: (section: HomeSection) => void;
  setIconOverrides: (overrides: Record<string, string>) => void;
  /** Move a tile to a new section and/or order position */
  moveTile: (tileId: string, toSection: string, toOrder: number) => void;
  /** Reorder tiles within a section (swap two tiles' orders) */
  reorderTiles: (sectionId: string, orderedTileIds: string[]) => void;
  /** Reset layout to default (clear all user customizations) */
  resetLayout: () => void;
  getSections: () => HomeSection[];
  getTilesBySection: (sectionId: string) => HomeTile[];
  _initDefaultSections: () => void;
}


export const useHomeScreenStore = create<HomeScreenState>()(
  devtools(
    persist(
      (set, get) => ({
        items: new Map(),
        sections: new Map(),
        iconOverrides: {},
        tileLayout: {},

        _initDefaultSections: () => {
          // Skip if sections already exist (e.g. restored from persist hydration)
          // to avoid wiping hydrated section data with empty placeholders.
          if (get().sections.size > 0) return;

          const defaultSections: HomeSection[] = [
            { id: 'main', title: undefined, order: 0, items: [] },
            { id: 'dock', title: undefined, order: 1, items: [] },
          ];

          const newSections = new Map<string, HomeSection>();
          defaultSections.forEach(section => {
            newSections.set(section.id, section);
          });

          set({ sections: newSections });
        },

        // Register tile (immutable updates - always creates new section objects)
        // Automatically applies icon overrides and user layout customizations.
        //
        // Idempotent: if the resolved tile is equivalent to the one already in
        // the store, no state update is dispatched. This keeps refetches
        // (catalog sync, plugin registry refresh, layout sync) from causing
        // the home grid to re-render and re-sort.
        registerTile: (tile: HomeTile) => {
          set((state) => {
            // Apply icon override if one exists for this tile
            const override = state.iconOverrides[tile.id];
            let resolvedTile = override ? { ...tile, icon: override } : tile;

            // Apply user layout customization if it exists
            const layoutEntry = state.tileLayout[tile.id];
            if (layoutEntry) {
              resolvedTile = {
                ...resolvedTile,
                section: layoutEntry.section as HomeTile['section'],
                order: layoutEntry.order,
              };
            }

            // Short-circuit when nothing actually changed.
            if (tilesEquivalent(state.items.get(resolvedTile.id), resolvedTile)) {
              return state;
            }

            logger.debug(`Registering tile: ${tile.id}`);

            const newItems = new Map(state.items);
            const newSections = new Map<string, HomeSection>();

            newItems.set(resolvedTile.id, resolvedTile);

            // Create new section objects to preserve immutability
            const sectionId = resolvedTile.section || 'main';
            state.sections.forEach((section, key) => {
              const filtered = section.items.filter(existing => existing.id !== resolvedTile.id);
              if (key === sectionId) {
                const items = [...filtered, resolvedTile].sort((a, b) => (a.order || 0) - (b.order || 0));
                newSections.set(key, { ...section, items });
              } else if (filtered.length !== section.items.length) {
                newSections.set(key, { ...section, items: filtered });
              } else {
                newSections.set(key, section);
              }
            });

            return { items: newItems, sections: newSections };
          });

          return () => get().unregisterTile(tile.id);
        },

        // Register multiple tiles in a single Zustand update (batch).
        // Idempotent: if every tile resolves to a value equivalent to what's
        // already in the store, no state update is dispatched.
        registerTiles: (tiles: HomeTile[]) => {
          if (tiles.length === 0) return;

          set((state) => {
            // First pass: resolve every tile and check whether anything changed.
            const resolved: HomeTile[] = [];
            let anyChanged = false;
            for (const tile of tiles) {
              const override = state.iconOverrides[tile.id];
              let resolvedTile = override ? { ...tile, icon: override } : tile;

              const layoutEntry = state.tileLayout[tile.id];
              if (layoutEntry) {
                resolvedTile = {
                  ...resolvedTile,
                  section: layoutEntry.section as HomeTile['section'],
                  order: layoutEntry.order,
                };
              }

              resolved.push(resolvedTile);
              if (!tilesEquivalent(state.items.get(resolvedTile.id), resolvedTile)) {
                anyChanged = true;
              }
            }

            if (!anyChanged) return state;

            logger.debug(`Batch registering ${tiles.length} tiles (${resolved.filter((t) => !tilesEquivalent(state.items.get(t.id), t)).length} changed)`);

            const newItems = new Map(state.items);
            const newSections = new Map<string, HomeSection>();

            // Copy existing sections (items arrays will be replaced below)
            state.sections.forEach((section, key) => {
              newSections.set(key, { ...section, items: [...section.items] });
            });

            for (const resolvedTile of resolved) {
              newItems.set(resolvedTile.id, resolvedTile);

              const sectionId = resolvedTile.section || 'main';
              newSections.forEach((section, key) => {
                const filtered = section.items.filter(existing => existing.id !== resolvedTile.id);
                if (key === sectionId) {
                  section.items = [...filtered, resolvedTile];
                } else {
                  section.items = filtered;
                }
              });
            }

            // Sort items in each section
            newSections.forEach((section) => {
              section.items.sort((a, b) => (a.order || 0) - (b.order || 0));
            });

            return { items: newItems, sections: newSections };
          });
        },

        // Unregister tile (immutable updates). Idempotent: no-op when the
        // tile isn't registered.
        unregisterTile: (tileId: string) => {
          set((state) => {
            if (!state.items.has(tileId)) return state;

            const newItems = new Map(state.items);
            newItems.delete(tileId);

            const newSections = new Map<string, HomeSection>();
            state.sections.forEach((section, key) => {
              const filtered = section.items.filter(item => item.id !== tileId);
              if (filtered.length !== section.items.length) {
                newSections.set(key, { ...section, items: filtered });
              } else {
                newSections.set(key, section);
              }
            });

            return { items: newItems, sections: newSections };
          });
        },

        // Store icon overrides and re-apply them to existing tiles.
        // Idempotent: if no override actually changes any tile's icon and the
        // overrides map matches the current one, the state update is skipped.
        setIconOverrides: (overrides: Record<string, string>) => {
          set((state) => {
            const newItems = new Map(state.items);
            let anyTileChanged = false;

            for (const [tileId, icon] of Object.entries(overrides)) {
              const existing = newItems.get(tileId);
              if (existing && existing.icon !== icon) {
                newItems.set(tileId, { ...existing, icon });
                anyTileChanged = true;
              }
            }

            // Compare overrides map for equality with the current one
            const prevKeys = Object.keys(state.iconOverrides);
            const nextKeys = Object.keys(overrides);
            const overridesChanged =
              prevKeys.length !== nextKeys.length ||
              nextKeys.some((k) => state.iconOverrides[k] !== overrides[k]);

            if (!anyTileChanged && !overridesChanged) return state;

            const newSections = new Map<string, HomeSection>();
            state.sections.forEach((section, key) => {
              if (anyTileChanged) {
                const updatedItems = section.items.map((item) => {
                  const updated = newItems.get(item.id);
                  return updated || item;
                });
                newSections.set(key, { ...section, items: updatedItems });
              } else {
                newSections.set(key, section);
              }
            });

            return { iconOverrides: overrides, items: newItems, sections: newSections };
          });
        },

        // Move a tile to a different section and/or order
        moveTile: (tileId: string, toSection: string, toOrder: number) => {
          set((state) => {
            const tile = state.items.get(tileId);
            if (!tile) return state;

            const updatedTile: HomeTile = {
              ...tile,
              section: toSection as HomeTile['section'],
              order: toOrder,
            };

            const newItems = new Map(state.items);
            newItems.set(tileId, updatedTile);

            // Rebuild sections
            const newSections = new Map<string, HomeSection>();
            state.sections.forEach((section, key) => {
              const filtered = section.items.filter((item) => item.id !== tileId);
              if (key === toSection) {
                const items = [...filtered, updatedTile].sort(
                  (a, b) => (a.order || 0) - (b.order || 0)
                );
                newSections.set(key, { ...section, items });
              } else {
                newSections.set(key, { ...section, items: filtered });
              }
            });

            // Persist layout customization
            const newLayout: TileLayout = {
              ...state.tileLayout,
              [tileId]: { section: toSection, order: toOrder },
            };

            return { items: newItems, sections: newSections, tileLayout: newLayout };
          });
        },

        // Reorder tiles within a section by providing the new order of tile IDs
        reorderTiles: (sectionId: string, orderedTileIds: string[]) => {
          set((state) => {
            const section = state.sections.get(sectionId);
            if (!section) return state;

            const newItems = new Map(state.items);
            const newLayout: TileLayout = { ...state.tileLayout };

            // Assign new orders based on array position
            const reorderedItems = orderedTileIds
              .map((id, index) => {
                const tile = section.items.find((t) => t.id === id);
                if (!tile) return null;
                const updated = { ...tile, order: index };
                newItems.set(id, updated);
                newLayout[id] = { section: sectionId, order: index };
                return updated;
              })
              .filter(Boolean) as HomeTile[];

            // Include any items not in orderedTileIds (shouldn't happen, but safe)
            const reorderedIds = new Set(orderedTileIds);
            const remaining = section.items
              .filter((t) => !reorderedIds.has(t.id))
              .map((t, i) => ({ ...t, order: orderedTileIds.length + i }));

            const newSections = new Map<string, HomeSection>();
            state.sections.forEach((sec, key) => {
              if (key === sectionId) {
                newSections.set(key, {
                  ...sec,
                  items: [...reorderedItems, ...remaining].sort(
                    (a, b) => (a.order || 0) - (b.order || 0)
                  ),
                });
              } else {
                newSections.set(key, { ...sec });
              }
            });

            return { items: newItems, sections: newSections, tileLayout: newLayout };
          });
        },

        // Reset all user layout customizations
        resetLayout: () => {
          set({ tileLayout: {} });
          // Re-register all tiles with their original positions
          const state = get();
          state.items.forEach((tile) => {
            state.registerTile(tile);
          });
        },

        registerSection: (section: HomeSection) => {
          set((state) => {
            const newSections = new Map(state.sections);
            newSections.set(section.id, section);
            return { sections: newSections };
          });
        },

        getSections: () => {
          const state = get();
          return Array.from(state.sections.values())
            .sort((a, b) => a.order - b.order);
        },

        getTilesBySection: (sectionId: string) => {
          const state = get();
          const section = state.sections.get(sectionId);
          return section?.items || [];
        },
      }),
      {
        name: 'homescreen-storage',
        storage: createJSONStorage(() => localStorage),
        partialize: (state) => ({
          iconOverrides: state.iconOverrides,
          tileLayout: state.tileLayout,
        }),
      }
    ),
    { name: 'HomeScreenStore' }
  )
);

useHomeScreenStore.getState()._initDefaultSections();

export function useHomeSections() {
  return useHomeScreenStore((state) => state.getSections());
}

export function useHomeSection(sectionId: string) {
  return useHomeScreenStore((state) => state.getTilesBySection(sectionId));
}

export function useRegisterHomeTile() {
  return useHomeScreenStore((state) => state.registerTile);
}

export function registerHomeTile(tile: HomeTile) {
  return useHomeScreenStore.getState().registerTile(tile);
}

export function registerHomeTiles(tiles: HomeTile[]) {
  return useHomeScreenStore.getState().registerTiles(tiles);
}
